// Barra de consumo de un presupuesto (`.progress-track` del mock): se pinta warn desde 70% y
// danger desde 90%. Debajo van el consumido a la izquierda y el porcentaje del tope a la derecha.
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type BudgetTone = 'normal' | 'warn' | 'danger';

const WARN_PCT = 70;
const DANGER_PCT = 90;

export function budgetTone(percent: number): BudgetTone {
    if (percent >= DANGER_PCT) return 'danger';
    if (percent >= WARN_PCT) return 'warn';
    return 'normal';
}

const FILL_CLASS: Record<BudgetTone, string> = {
    normal: 'bg-primary',
    warn: 'bg-warning',
    danger: 'bg-danger',
};

export function BudgetBar({
    consumed,
    max,
    label,
    start,
    end,
    className,
}: {
    /** Lo gastado, en la misma unidad que `max`. */
    consumed: number;
    /** El tope; mayor que cero. */
    max: number;
    /** Cómo la nombra un lector de pantalla. */
    label: string;
    /** Texto bajo la barra, a la izquierda (p. ej. «$2,500 consumido»). */
    start?: ReactNode;
    /** Texto bajo la barra, a la derecha (p. ej. «25% de $10,000»). */
    end?: ReactNode;
    className?: string;
}) {
    const percent = max > 0 ? Math.min(100, Math.round((consumed / max) * 100)) : 0;
    const tone = budgetTone(percent);
    return (
        <div className={className}>
            <div
                role="progressbar"
                aria-label={label}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent}
                data-tone={tone}
                className="mt-1.5 h-1.5 overflow-hidden rounded-[3px] bg-muted"
            >
                <div
                    className={cn('h-full transition-[width]', FILL_CLASS[tone])}
                    style={{ width: `${String(percent)}%` }}
                />
            </div>
            {start !== undefined || end !== undefined ? (
                <div className="mt-[3px] flex justify-between text-[10.5px] tabular-nums text-muted-foreground">
                    <span>{start}</span>
                    <span>{end}</span>
                </div>
            ) : null}
        </div>
    );
}
